"use client";

import { useId, useRef, useState } from "react";
import { VALIDATION_DIMENSIONS, DIMENSION_LABEL, type ValidationDimension } from "@/lib/validation-schema";

type Ratings = Partial<Record<ValidationDimension, number>>;

export default function ValidationForm({ parkId }: { parkId: string }) {
  const [ratings, setRatings] = useState<Ratings>({});
  const [confidence, setConfidence] = useState(0.7);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [done, setDone] = useState(false);
  const confidenceId = useId();
  const errorRef = useRef<HTMLParagraphElement | null>(null);

  const set = (dim: ValidationDimension, v: number) => setRatings((r) => ({ ...r, [dim]: v }));

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setMsg("");
    try {
      const r = await fetch("/api/validation", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ parkId, ratings, confidence }),
      });
      if (!r.ok) {
        setMsg(
          r.status === 429
            ? "Too many submissions from this connection. Wait a minute, then try again."
            : "We could not save your ratings. Check your connection and submit again.",
        );
        requestAnimationFrame(() => errorRef.current?.focus());
      } else {
        setDone(true);
      }
    } catch {
      setMsg("We could not reach the server. Check your connection and submit again.");
      requestAnimationFrame(() => errorRef.current?.focus());
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <div
        role="status"
        aria-live="polite"
        className="rounded-md border border-outline-variant/60 bg-primary-fixed/40 p-3 font-body-md text-body-md text-on-surface"
      >
        Thanks. Your ratings are aggregated with everyone else&apos;s. No row is published individually.
      </div>
    );
  }

  return (
    <form onSubmit={submit} aria-busy={busy} className="space-y-4">
      <p className="font-body-md text-body-md text-on-surface-variant">
        Rate this park on as many dimensions as you have an opinion about. 1 = not at all, 5 = strongly. Skip
        the ones you don&apos;t feel sure about. Aggregated only, no comments stored at the row level.
      </p>
      <fieldset disabled={busy} className="m-0 space-y-4 border-0 p-0">
        <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
          {VALIDATION_DIMENSIONS.map((dim) => (
            <div
              key={dim}
              role="group"
              aria-label={DIMENSION_LABEL[dim]}
              className="rounded-md border border-outline-variant/60 bg-surface-container-low p-3"
            >
              <div className="font-body-md text-body-md font-semibold capitalize text-on-surface">
                {DIMENSION_LABEL[dim]}
              </div>
              <div className="mt-1.5 flex gap-1">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => set(dim, n)}
                    className={`flex h-8 w-8 items-center justify-center rounded border font-body-md text-body-md font-medium disabled:opacity-60 ${
                      ratings[dim] === n
                        ? "border-primary bg-primary text-on-primary"
                        : "border-outline-variant bg-surface-container-lowest hover:bg-surface-container-high"
                    }`}
                    aria-label={`${DIMENSION_LABEL[dim]} ${n} of 5`}
                    aria-pressed={ratings[dim] === n}
                  >
                    {n}
                  </button>
                ))}
                {ratings[dim] !== undefined && (
                  <button
                    type="button"
                    onClick={() => setRatings((r) => { const cp = { ...r }; delete cp[dim]; return cp; })}
                    className="ml-2 self-center font-body-md text-body-md text-on-surface-variant hover:underline disabled:opacity-60"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <label htmlFor={confidenceId} className="font-body-md text-body-md text-on-surface-variant">
            How confident are you?
          </label>
          <input
            id={confidenceId}
            type="range"
            min={0.2}
            max={1}
            step={0.1}
            value={confidence}
            onChange={(e) => setConfidence(Number(e.target.value))}
            className="w-40 accent-primary"
          />
          <span className="font-body-md text-body-md tabular-nums text-on-surface-variant">
            {Math.round(confidence * 100)}%
          </span>
        </div>
      </fieldset>

      <div className="flex items-center gap-3">
        <button
          type="submit"
          disabled={busy || Object.keys(ratings).length === 0}
          className="rounded-md bg-primary px-3 py-1.5 font-body-md text-body-md font-semibold text-on-primary hover:opacity-90 disabled:opacity-50"
        >
          {busy ? "Submitting the ratings" : "Submit ratings"}
        </button>
      </div>

      {/* Submit-level errors in a polite live region; tabIndex -1 so focus
          can move here when the request resolves. */}
      <p ref={errorRef} tabIndex={-1} aria-live="polite" className="outline-none">
        {msg && (
          <span className="block rounded-md bg-error-container/60 p-2 font-body-md text-body-md text-on-error-container">
            {msg}
          </span>
        )}
      </p>
    </form>
  );
}
